import { Trans, useLingui } from "@lingui/react/macro";
import { providerFullDisplayName } from "@pontimed/types";
import { apiFetch } from "@pontimed/api-client";
import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { MessageSquare, Star, ThumbsUp, TrendingUp } from "lucide-react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import {
  FacilityStateCard,
  facilityErrorKind,
} from "@/components/account/facility/facility-state-card";
import { PageHeader } from "@/components/layout/page-header";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StatCard } from "@/components/ui/stat-card";

const PAGE_SIZE = 10;

interface ReviewRow {
  id: string;
  rating: number;
  ratingPunctuality: number | null;
  ratingAttitude: number | null;
  text: string | null;
  createdAt: string;
  providerResponse: string | null;
  providerRespondedAt: string | null;
  profile: { firstName: string; lastName: string; photoUrl: string | null } | null;
  provider: { firstName: string; lastName: string; profession?: string | null } | null;
}

interface FacilityReviewsResponse {
  reviews: ReviewRow[];
  total: number;
  page: number;
  hasMore: boolean;
  stats: {
    avg: number | null;
    total: number;
    distribution: Record<string, number>;
    responded: number;
    responseRate: number;
    thisMonth: number;
    lastMonth: number;
  };
}

export const Route = createFileRoute("/_authenticated/account/facility/$facilityId/reviews")({
  component: FacilityReviewsPage,
});

function FacilityReviewsPage() {
  const { facilityId } = Route.useParams();
  const { i18n, t } = useLingui();
  const locale = i18n.locale;

  const {
    data,
    error,
    isPending,
    isError,
    isFetching,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["api", `/api/facilities/${facilityId}/reviews`],
    queryFn: ({ pageParam, signal }) =>
      apiFetch.get<FacilityReviewsResponse>(
        `/api/facilities/${facilityId}/reviews?page=${pageParam}&limit=${PAGE_SIZE}`,
        { signal, skipAuthRefresh: true },
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
  });

  if (isPending) return <ReviewsSkeleton />;
  if (isError || !data) {
    return (
      <FacilityStateCard
        kind={isError ? facilityErrorKind(error) : "load-failed"}
        facilityId={facilityId}
        onRetry={() => void refetch()}
        retrying={isFetching}
      />
    );
  }

  const stats = data.pages[0].stats;
  const reviews = data.pages.flatMap((p) => p.reviews);

  const ratingValue = stats.avg != null ? Number(stats.avg) : null;
  const respondedCount = stats.responded;

  // Month trend (legacy logic: this month vs last month).
  const count = stats.thisMonth - stats.lastMonth;
  let monthTrendValue: string;
  if (stats.total === 0) monthTrendValue = "—";
  else if (stats.thisMonth === 0 && stats.lastMonth === 0)
    monthTrendValue = t`No change this month`;
  else if (count > 0) monthTrendValue = t`+${count} this month`;
  else if (count < 0) monthTrendValue = String(count);
  else monthTrendValue = t`No change this month`;

  // Build a normalized 5-bucket distribution from the Record<1..5, count>.
  const buckets = [5, 4, 3, 2, 1].map((rating) => ({
    rating,
    count: stats.distribution[String(rating)] ?? 0,
  }));
  const maxCount = Math.max(1, ...buckets.map((b) => b.count));

  return (
    <div className="space-y-5">
      <PageHeader
        title={<Trans>Reviews</Trans>}
        description={<Trans>What patients say about this facility.</Trans>}
      />

      {/* Top KPI row */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard
          label={<Trans>Average</Trans>}
          value={ratingValue ?? "—"}
          format={{ minimumFractionDigits: 1, maximumFractionDigits: 1 }}
          icon={Star}
          tone="warning"
          hint={
            <span className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((star) => {
                const filled = stats.avg != null && star <= Math.round(Number(stats.avg));
                return (
                  <Star
                    key={star}
                    className={`size-3.5 ${filled ? "fill-warning text-warning" : "text-muted-foreground/30"}`}
                    aria-hidden
                  />
                );
              })}
            </span>
          }
        />
        <StatCard
          label={<Trans>Total reviews</Trans>}
          value={stats.total > 0 ? stats.total : "—"}
          icon={MessageSquare}
          tone="primary"
        />
        <StatCard
          label={<Trans>Response rate</Trans>}
          value={stats.total > 0 ? stats.responseRate / 100 : "—"}
          format={{ style: "percent", maximumFractionDigits: 0 }}
          icon={ThumbsUp}
          tone="accent"
        />
        <StatCard
          label={<Trans>Monthly trend</Trans>}
          value={monthTrendValue}
          icon={TrendingUp}
          tone="success"
        />
      </div>

      {/* Rating distribution */}
      <div className="border-border bg-card rounded-xl border p-5">
        <p className="text-muted-foreground mb-3 text-xs font-medium tracking-wider uppercase">
          <Trans>Distribution</Trans>
        </p>
        <ul className="space-y-1.5">
          {buckets.map((b) => (
            <li key={b.rating} className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground inline-flex w-5 shrink-0 items-center justify-end font-semibold">
                {b.rating}
              </span>
              <Star className="fill-warning text-warning size-3 shrink-0" aria-hidden />
              <div className="bg-muted h-1.5 flex-1 overflow-hidden rounded-full">
                <div
                  className="bg-warning h-full rounded-full transition-all"
                  style={{ width: `${(b.count / maxCount) * 100}%` }}
                />
              </div>
              <span className="text-muted-foreground w-8 shrink-0 text-right tabular-nums">
                {b.count}
              </span>
            </li>
          ))}
        </ul>
        <p className="text-muted-foreground mt-3 text-xs">
          <Trans>{respondedCount} responded by provider</Trans>
        </p>
      </div>

      {/* Recent reviews list */}
      {reviews.length === 0 ? (
        <div className="border-border bg-card flex flex-col items-center gap-2 rounded-xl border py-12 text-center">
          <MessageSquare className="text-muted-foreground/40 size-8" aria-hidden />
          <p className="text-muted-foreground text-sm">
            <Trans>No reviews yet.</Trans>
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {reviews.map((review) => {
            const author = review.profile
              ? `${review.profile.firstName} ${review.profile.lastName.slice(0, 1)}.`
              : t`Anonymous`;
            const provider = review.provider
              ? providerFullDisplayName(review.provider, locale)
              : null;
            const date = new Date(review.createdAt).toLocaleDateString(locale, {
              day: "2-digit",
              month: "short",
              year: "numeric",
            });
            return (
              <li key={review.id} className="border-border bg-card space-y-2 rounded-xl border p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-foreground text-sm font-medium">{author}</p>
                    <p className="text-muted-foreground text-xs">
                      {provider ? `${provider} · ` : ""}
                      {date}
                    </p>
                  </div>
                  <div className="flex flex-shrink-0 gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => {
                      const filled = star <= review.rating;
                      return (
                        <Star
                          key={star}
                          className={`size-3.5 ${filled ? "fill-warning text-warning" : "text-muted-foreground/30"}`}
                          aria-hidden
                        />
                      );
                    })}
                  </div>
                </div>
                {(review.ratingPunctuality != null || review.ratingAttitude != null) && (
                  <div className="flex flex-wrap gap-2">
                    {review.ratingPunctuality != null && (
                      <span className="bg-muted text-muted-foreground inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs">
                        <Trans>Punctuality</Trans>: {review.ratingPunctuality}/5
                      </span>
                    )}
                    {review.ratingAttitude != null && (
                      <span className="bg-muted text-muted-foreground inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs">
                        <Trans>Attitude</Trans>: {review.ratingAttitude}/5
                      </span>
                    )}
                  </div>
                )}
                {review.text && (
                  <p className="text-foreground text-sm whitespace-pre-line">{review.text}</p>
                )}
                {review.providerResponse && (
                  <div className="bg-muted/40 border-border/60 border-l-primary mt-2 rounded-lg border-l-2 p-3">
                    <p className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
                      <Trans>Provider response</Trans>
                      {review.providerRespondedAt && (
                        <span className="ml-2 font-normal normal-case">
                          ·{" "}
                          {new Date(review.providerRespondedAt).toLocaleDateString(locale, {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </span>
                      )}
                    </p>
                    <p className="text-foreground mt-1 text-sm whitespace-pre-line">
                      {review.providerResponse}
                    </p>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="min-h-11"
          >
            <Trans>Load more</Trans>
          </Button>
        </div>
      )}

      <p className="text-muted-foreground text-center text-xs">
        <Trans>Respond to reviews ships V1.1.</Trans>
      </p>

      <AccountSecurityBanner />
    </div>
  );
}

function ReviewsSkeleton() {
  return (
    <div className="space-y-5">
      <Skeleton className="h-10 w-48" />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-28 rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-40 rounded-xl" />
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-32 rounded-xl" />
      ))}
    </div>
  );
}
